import { FREE_GIFT_CLASSES } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { ProductWithBubble } from '@/components/blocks/FreeGiftV2/parts/ProductWithBubble';
import { RewardBlockSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Badge, Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';

interface RewardBlockProps {
	sx?: SxProps<Theme>;
}

export const RewardBlock = ({ sx }: RewardBlockProps) => {
	const theme = useTheme();
	const { selectedRewardSkus, isPromotionComplete, promotionQty } = useFreeGiftInteraction();
	const { rewardsQty, reward, currentPartNumber } = useFreeGiftPromotion();
	const isCombinable = reward?.combinable;
	const labels = useLocalization('FreeGift');

	const getDisplayQuantity = (quantity: number) =>
		isPromotionComplete ? quantity : Math.max(1, Math.round(quantity / promotionQty));

	const orderedRewardSkus = useMemo(
		() =>
			[...selectedRewardSkus].sort(
				(a, b) => Number(b.sku === currentPartNumber) - Number(a.sku === currentPartNumber)
			),
		[selectedRewardSkus, currentPartNumber]
	);

	const userHasSelectedGifts = selectedRewardSkus.length > 0;
	const totalSelectedRewardQty = selectedRewardSkus.reduce((sum, sku) => sum + sku.quantity, 0);

	const noCombinableGiftQuantities = !isCombinable
		? (reward?.rewards ?? []).map((group) => group.quantity)
		: [];
	const hasSameQtyAcrossGiftOptions = noCombinableGiftQuantities.every(
		(qty) => qty === noCombinableGiftQuantities[0]
	);
	const noCombinableGiftDisplayQty = hasSameQtyAcrossGiftOptions
		? noCombinableGiftQuantities[0]
		: 1;

	const missingGiftQty = isCombinable
		? rewardsQty - totalSelectedRewardQty
		: userHasSelectedGifts
		? 0
		: noCombinableGiftDisplayQty;

	return (
		<Box
			component="fieldset"
			sx={[RewardBlockSx.fieldset, ...(Array.isArray(sx) ? sx : [sx])]}
			className={FREE_GIFT_CLASSES.fpGift}
		>
			<Typography
				component="legend"
				p="3px"
				color="success.main"
				fontWeight={OpenSansFontWeights.semibold}
				variant="bodySubText"
			>
				{labels.TakeFree.t()}
			</Typography>
			<Box display="flex" gap="5px" alignItems="center">
				{orderedRewardSkus.slice(0, 3).map((sku: SkuQuantity) => (
					<ProductWithBubble
						key={sku.sku}
						partNumber={sku.sku}
						quantity={getDisplayQuantity(sku.quantity)}
					/>
				))}
				{missingGiftQty > 0 ? (
					<>
						{userHasSelectedGifts ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS.add}
								color={theme.palette.grey[500]}
								size="30px"
							/>
						) : null}
						<Box sx={RewardBlockSx.missingItemsBox}>
							{missingGiftQty === 1 ? (
								<DynamicIcon
									name={MATERIAL_SYMBOL_ICONS.featuredSeasonalAndGifts}
									color={theme.palette.success.main}
									size="40px"
								/>
							) : (
								<Badge
									badgeContent={missingGiftQty}
									color="success"
									overlap="circular"
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									sx={RewardBlockSx.badge}
								>
									<DynamicIcon
										name={MATERIAL_SYMBOL_ICONS.featuredSeasonalAndGifts}
										color={theme.palette.success.main}
										size="40px"
									/>
								</Badge>
							)}
						</Box>
					</>
				) : null}
				{selectedRewardSkus?.length > 3 ? (
					<Typography p="3px" color="grey.500" variant="headingSubtitle">
						+{selectedRewardSkus?.length - 3}
					</Typography>
				) : null}
			</Box>
		</Box>
	);
};
